/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Editor, EditorState, ContentState } from 'draft-js';
import { OrderedSet } from 'immutable';
import actions from '@src/redux/actions';
import classNames from 'classnames';
import { Popper } from '@mui/material';
import {
  KeyboardArrowDownOutlined,
  // KeyboardArrowUpOutlined,
} from '@mui/icons-material';
import PreviewButton from '@src/components/PreviewButton';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import { BREAK_TIME_REGEX, REGEX } from '@src/constants/voice';
import { countTextLength } from '@src/services/tts';
import { getSelectedText } from '@src/services/sentence';
import { generateDecorator } from '@src/services/editor';
import {
  handleEditParagraphs,
  convertParagraphsToState,
  handleSelectedParagraph,
  countEditorStateLength,
  convertTextToParagraphs,
  convertParagraphsToText,
} from '@src/services/paragraph';
import Tooltip from '@src/components/Tooltip';
import { handleSelection } from '@src/services/entity';
import debounce from '@src/utils/debounce';

import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import { StyledMapEditor } from '@src/containers/TTS/index.style';

import {
  // TTS_BOX_SIZE,
  TTS_VERSION,
} from '@src/constants/tts';
import PreviewButtonWithDescription from '@src/components/PreviewButtonWithDescription';
import AIEditorButton from '@src/components/AIEditorButton';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import apis from '@src/apis';
import { checkVietNam } from '@src/utils/checkCountry';
import useUserInfo from '@src/hooks/useTtsUser';
import { usePronunciationContext } from '@src/contexts/PronunciationCheckContext';
import PronunciationSideBox from '../PronunciationSideBox';
import PronunciationChip from '../PronunciationChip';
import PronunciationCheckDialogs from '../PronunciationCheckDialogs';
import Sentences from './Sentences';

import {
  StyledContent,
  StyledPreviewButton,
  StyledPreviewButtonNew,
  StyledEditorWithSidebar,
} from './index.style';
import SampleScripts from '../SampleScripts';
import { StyledIconButton } from '../Requests/index.style';

const Content = ({
  children,
  openSentences,
  content,
  isSmallContent,
  inputLength,
  characterExceed,
  onChangeContent,
  requestTableHeight,
  onChangeInputLength,
  onChangeCharacterExceed,
  onchangeSynthesisSentence,
  onTryListeningSentence,
  onChangeVoice,
  audioRef,
  synthesisSentence,
  enableEditor,
  onEnableEditor,
  // onResetContentEditor,
  onChangeExpandRequestTable,
}) => {
  const [isInputText, setIsInputText] = useState(false);
  const [isHaveScroll, setIsHaveScroll] = useState(false);
  const [sampleScripts, setSampleScripts] = useState([]);
  const [defaultSampleScript, setDefaultSampleScript] = useState(null);
  const boxRef = useRef(null);
  const {
    synthesisRequest,
    sentences,
    loadingSynthesisConfig,
    paragraphs,
    isLoadingParagraphs,
    advanceFeature,
    showSample,
    previewParagraphs: previewContent,
    showBannerProjects,
  } = useSelector((state) => state.synthesisRequest);
  const user = useSelector((state) => state.auth.user);
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();
  const { isAudioLoading, popupPreviewAnchorEl, showAudioPlayer } = useSelector(
    (state) => state.audioPlayer,
  );

  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;
  const isVietNam = checkVietNam();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ttsRef = useRef(null);
  const { headerVoucher } = useSelector((state) => state.voucher);
  const { getFeatureValue } = useFeatureFlags();

  const aiPronunciationCheckEnabled = getFeatureValue(
    FEATURE_KEYS.AI_PRONUNCIATION_CHECK,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const {
    isOpen: isPronunciationOpen,
    dialogType,
    closeDialog,
    confirmRecheck,
    isLoading,
  } = usePronunciationContext();

  const handleChangePopupPreviewAnchorEl = (value) => {
    dispatch(actions.audioPreview.updatePopupPreviewAnchorEl(value));
  };

  const locationRouter = useLocation();

  const canPreview = advanceFeature
    ? previewContent?.length && usingPackage.id && !isAudioLoading
    : synthesisSentence && usingPackage.id && !isAudioLoading;

  const fetchSampleScript = async () => {
    const response = await apis.sampleScripts.getSampleScript({
      language: isVietNam ? 'vi' : 'en',
    });
    const totalSampleScript = response?.result?.sampleScripts;
    let sampleScript = [];
    if (isVietNam)
      sampleScript = totalSampleScript.filter(
        (item) => item.isVietnamese && !item.isDefault,
      );
    else
      sampleScript = totalSampleScript.filter(
        (item) => !item.isVietnamese && !item.isDefault,
      );
    setSampleScripts(sampleScript);

    const defaultSample = totalSampleScript.find(
      (item) =>
        item.isDefault && (isVietNam ? item.isVietnamese : !item.isVietnamese),
    );
    setDefaultSampleScript(defaultSample);
    dispatch(actions.synthesisRequest.updateDefaultSample(defaultSample));
  };

  useEffect(() => {
    fetchSampleScript();
  }, [isVietNam]);

  const handleChangeEditor = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const lengthContent = countTextLength(contentState.getPlainText());

    if (contentState.getPlainText() !== synthesisRequest?.text) {
      onEnableEditor(true);
      if (!enableEditor)
        dispatch(actions.synthesisRequest.cloneSynthesisRequest(true));
    }

    onChangeInputLength(lengthContent);

    let newContent = editorState;
    const currentStyle = editorState.getCurrentInlineStyle();
    if (currentStyle && currentStyle.size) {
      newContent = EditorState.setInlineStyleOverride(
        editorState,
        OrderedSet.of(),
      );
    }
    onChangeContent(newContent);

    if (lengthContent > maxLengthInputText) {
      onChangeCharacterExceed(true);
    } else {
      onChangeCharacterExceed(false);
    }

    const selectedText = getSelectedText(editorState);
    onchangeSynthesisSentence(selectedText);
  };

  // Define event when editor state changes
  const handleChangeEditorState = (newEditorState) => {
    const selectionState = newEditorState.getSelection();
    // If dont focus on the editor, cant do anything
    if (!selectionState.toJS().hasFocus) {
      onChangeContent(newEditorState);
      return;
    }
    if (!isInputText) setIsInputText(true);

    const contentState = newEditorState.getCurrentContent();

    const currentPlainText = synthesisRequest?.text
      ?.replace(REGEX.ADVANCE_TAG, '')
      .trim();
    const isTextModifiedNew =
      convertParagraphsToText(paragraphs) !== synthesisRequest?.text;
    const isTextModifiedOld =
      contentState.getPlainText()?.trim() !== currentPlainText;

    const compareText = advanceFeature ? isTextModifiedNew : isTextModifiedOld;
    if (compareText) {
      onEnableEditor(true);
      if (!enableEditor)
        dispatch(actions.synthesisRequest.cloneSynthesisRequest(true));
    }

    // Selection area when the user highlights
    const selection = handleSelection({
      selectionState,
      contentState,
      paragraphs,
    });
    if (selection) {
      onChangeContent(EditorState.forceSelection(newEditorState, selection));
      // previewParagraphs: preview data, format same paragraphs
      // selectedParagraphs: selected data, format same content editor state to support for emphasis, speed, break time
      const { previewParagraphs, selectedParagraph } = handleSelectedParagraph(
        selection,
        contentState,
        paragraphs,
      );
      dispatch(
        actions.synthesisRequest.updateSelectedParagraph(selectedParagraph),
      );
      dispatch(
        actions.synthesisRequest.updatePreviewParagraphs(previewParagraphs),
      );
    } else {
      dispatch(actions.synthesisRequest.updatePreviewParagraphs([]));
      onChangeContent(newEditorState);
    }
  };

  const handleChangeParagraphs = () => {
    // Update paragraphs when editor state changes
    const newParagraphs = handleEditParagraphs(paragraphs, content);
    if (newParagraphs !== null) {
      dispatch(actions.synthesisRequest.updateParagraphs(newParagraphs));

      const contentLength = countEditorStateLength(content, newParagraphs);

      onChangeInputLength(contentLength);

      if (contentLength > maxLengthInputText) {
        onChangeCharacterExceed(true);
      } else {
        onChangeCharacterExceed(false);
      }
    }
    setIsInputText(false);
  };

  const handleBlurEditor = () => {
    if (advanceFeature) {
      dispatch(actions.synthesisRequest.updatePreviewParagraphs([]));
    } else {
      onchangeSynthesisSentence();
    }
  };

  useEffect(() => {
    if (advanceFeature && isInputText)
      debounce(handleChangeParagraphs, 300)(content);
  }, [content]);

  useEffect(() => {
    // loadingSynthesisConfig: load again tts when user click to request in request table or change voice
    if (loadingSynthesisConfig) {
      const {
        text,
        sentences: synthesisConfigSentences,
        characters,
        voice,
        paragraphs: synthesisParagraphs = [],
        id,
      } = synthesisRequest;

      if (id) {
        onChangeInputLength(characters);
      }
      onChangeVoice(voice, loadingSynthesisConfig);

      // Reset audio
      // dispatch(
      //   actions.audioPlayer.updateMetaData({ currentTime: 0, duration: 0 }),
      // );
      // dispatch(actions.audioPlayer.updateStatus(false));
      // const audioLink = synthesisRequest.audioLink || '';
      // dispatch(actions.audioPlayer.updateAudioLink(audioLink));
      dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(false));

      const processBySentence = !!synthesisConfigSentences?.length;
      // When process method change => update open sentence
      if (processBySentence !== openSentences)
        dispatch(
          actions.synthesisRequest.updateOpenSentences(processBySentence),
        );

      // Sentences processing
      if (processBySentence) {
        const convertSentences = synthesisConfigSentences.map((item) => ({
          ...item,
          id: uuidv4(),
          onLoad: true,
          text: item.text,
        }));

        dispatch(actions.synthesisRequest.updateSentences(convertSentences));
        return;
      }

      // Paragraph process
      const isAdvanceFeature = voice?.version === TTS_VERSION.EMPHASIS;
      const isOldRequestUseEmphasisVoice =
        synthesisRequest?.voice?.version === TTS_VERSION.EMPHASIS ||
        synthesisRequest?.version === TTS_VERSION.EMPHASIS;

      if (isOldRequestUseEmphasisVoice) {
        const textRemovedAdvancedTags = text.replace(
          REGEX.ADVANCE_BREAK_TIME_TAG,
          (p1, p2) => `${`<break time=${p2}s/>`}`,
        );
        const contentEditor = EditorState.createWithContent(
          ContentState.createFromText(textRemovedAdvancedTags || ''),
          generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
        );
        const newEditorState = EditorState.moveFocusToEnd(contentEditor);
        onChangeContent(newEditorState);
        return;
      }

      if (!isAdvanceFeature) {
        const contentEditor = EditorState.createWithContent(
          ContentState.createFromText(text || ''),
          generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
        );
        const newEditorState = EditorState.moveFocusToEnd(contentEditor);
        onChangeContent(newEditorState);
      }

      if (isAdvanceFeature && isAdvanceFeature === advanceFeature) {
        dispatch(
          actions.synthesisRequest.updateParagraphs(synthesisParagraphs),
        );
        dispatch(actions.synthesisRequest.updateLoadingParagraphs(true));
      }

      if (isAdvanceFeature !== advanceFeature)
        dispatch(
          actions.synthesisRequest.switchAdvanceFeature(isAdvanceFeature),
        );
    }
  }, [loadingSynthesisConfig]);

  const handleShowContent = () => {
    let contentParagraphs = paragraphs;
    if (!paragraphs?.length) {
      contentParagraphs = convertTextToParagraphs(synthesisRequest?.text || '');
    }
    const contentState = convertParagraphsToState(contentParagraphs);
    onChangeContent(EditorState.push(content, contentState));
  };
  // const handleChangeTtsBoxSize = (value) => setTtsBoxSize(value);

  const handleResetContent = () => {
    dispatch(actions.synthesisRequest.updateParagraphs([]));
    dispatch(actions.synthesisRequest.updateSelectedSentences([]));
    dispatch(actions.synthesisRequest.updateOpenSentences(false));
    dispatch(actions.synthesisRequest.switchAdvanceFeature(false));
  };

  const placeholder = useMemo(
    () => defaultSampleScript?.text || '',
    [defaultSampleScript],
  );

  useEffect(() => {
    // isLoadingParagraphs: Load again editor state when paragraphs change
    if (isLoadingParagraphs) {
      handleShowContent();
      dispatch(actions.synthesisRequest.updateLoadingParagraphs(false));
    }
  }, [isLoadingParagraphs]);

  useEffect(() => {
    if (advanceFeature && !openSentences) {
      dispatch(
        actions.synthesisRequest.updateParagraphs(synthesisRequest?.paragraphs),
      );
      dispatch(actions.synthesisRequest.updateLoadingParagraphs(true));
    }
  }, [advanceFeature]);

  useEffect(() => {
    const box = boxRef.current;
    if (box.scrollHeight > box.clientHeight) {
      setIsHaveScroll(true);
    } else {
      setIsHaveScroll(false);
    }
  }, [inputLength, content, sentences.length]);

  useEffect(() => {
    dispatch(actions.synthesisRequest.updateLoadingParagraphs(false));
    return () => {
      handleResetContent();
    };
  }, [locationRouter]);

  useEffect(() => {
    if (!openSentences) {
      dispatch(actions.synthesisRequest.updateSelectedSentences([]));
    }
  }, [openSentences]);

  useEffect(() => {
    if (!canPreview) {
      handleChangePopupPreviewAnchorEl(null);
    }
  }, [synthesisSentence, previewContent]);

  const handlePastedText = () => {
    const currentScrollHeight = boxRef.current?.scrollHeight;
    setTimeout(() => {
      const newScrollHeight = boxRef.current?.scrollHeight;
      boxRef.current.scrollTop += newScrollHeight - currentScrollHeight;
    }, 100);
  };

  const handleTryListeningSentence = (sentence) => {
    handleChangePopupPreviewAnchorEl(null);
    onTryListeningSentence(sentence);
  };

  useEffect(() => {
    if (inputLength && showSample)
      dispatch(actions.synthesisRequest.updateShowSampleScripts(false));
  }, [inputLength]);

  React.useEffect(() => {
    const hiddenBannerUsers = localStorage.getItem('hiddenBannerUsers') || '[]';
    const userIds = JSON.parse(hiddenBannerUsers);
    if (userIds.includes(user?.id)) {
      dispatch(actions.synthesisRequest.updateShowBannerProjects(false));
    }
  }, [user, showBannerProjects]);

  return (
    <StyledContent
      ref={ttsRef}
      useNewTTSLayout
      requestTableHeight={requestTableHeight}
      showaudioplayer={showAudioPlayer}
      isSmallContent={isSmallContent}
      // id={TTS_GUIDE.ENTER_TEXT_HERE}
      openSentences={openSentences}
      showSample={showSample}
      headerVoucher={headerVoucher}
    >
      {openSentences ? (
        <Sentences
          advanceFeature={advanceFeature}
          isSmallContent={isSmallContent}
          useNewTTSLayout
          requestTableHeight={requestTableHeight}
          characterExceed={characterExceed}
          onChangeInputLength={onChangeInputLength}
          onChangeCharacterExceed={onChangeCharacterExceed}
          onTryListeningSentence={onTryListeningSentence}
          audioRef={audioRef}
          openSentences={openSentences}
          enableEditor={enableEditor}
          onEnableEditor={onEnableEditor}
          editorRef={boxRef}
          headerVoucher={headerVoucher}
        />
      ) : (
        <StyledEditorWithSidebar>
          <div
            // id="editor-wrapper"
            id={TTS_GUIDE.ENTER_TEXT_HERE}
            role="button"
            tabIndex={0}
            className={classNames(
              'editor-wrapper-new-layout editor-flex-area',
              {
                'character-exceed': characterExceed,
              },
            )}
            ref={boxRef}
            onBlur={handleBlurEditor}
          >
            <Editor
              placeholder={placeholder}
              editorState={content}
              onChange={
                advanceFeature ? handleChangeEditorState : handleChangeEditor
              }
              customStyleMap={StyledMapEditor}
              stripPastedStyles
              handlePastedText={handlePastedText}
            />
          </div>
          {isPronunciationOpen && <PronunciationSideBox />}
        </StyledEditorWithSidebar>
      )}
      <div className="preview-guide preview-guide-new-ui">
        {isSmallContent && isHaveScroll && !isLoading && (
          <Tooltip arrow title={t('expand')} placement="top">
            <StyledIconButton
              className="new-preview-guide-btn"
              onClick={onChangeExpandRequestTable}
            >
              <KeyboardArrowDownOutlined />
            </StyledIconButton>
          </Tooltip>
        )}
      </div>
      <StyledPreviewButtonNew>
        <div className="tool-row">
          <div className="ai-editor-wrapper">
            {aiPronunciationCheckEnabled && !showSample && (
              <>
                <AIEditorButton />
              </>
            )}
            {showSample ? (
              <SampleScripts
                audioRef={audioRef}
                sampleScripts={sampleScripts}
                onChangeInputLength={onChangeInputLength}
              />
            ) : (
              <PreviewButtonWithDescription
                synthesisRequest={synthesisRequest}
                synthesisSentence={synthesisSentence}
                openSentences={openSentences}
                onTryListeningSentence={onTryListeningSentence}
                audioRef={audioRef}
                canPreview={canPreview}
              />
            )}
          </div>
          {aiPronunciationCheckEnabled && <PronunciationChip />}
          {children}
        </div>
      </StyledPreviewButtonNew>
      {/* <RefreshButton
        inputLength={inputLength}
        enableEditor={enableEditor}
        onResetContentEditor={onResetContentEditor}
      /> */}
      <Popper
        open={Boolean(popupPreviewAnchorEl)}
        anchorEl={popupPreviewAnchorEl}
        placement="right"
        style={{ zIndex: 1, cursor: 'pointer' }}
      >
        <StyledPreviewButton>
          <PreviewButton
            synthesisRequest={synthesisRequest}
            synthesisSentence={synthesisSentence}
            onTryListeningSentence={handleTryListeningSentence}
            audioRef={audioRef}
            canPreview={canPreview}
            isPopupPreview
          />
        </StyledPreviewButton>
      </Popper>
      {/* <ChangeBoxTTSSizeButton
        isShow={isHaveScroll}
        ttsBoxSize={ttsBoxSize}
        toolbarRef={toolbarRef}
        onChangeTtsBoxSize={handleChangeTtsBoxSize}
          /> */}
      {dialogType && (
        <PronunciationCheckDialogs
          dialogType={dialogType}
          onClose={closeDialog}
          onConfirmRecheck={confirmRecheck}
          maxLengthInputText={maxLengthInputText}
        />
      )}
    </StyledContent>
  );
};

export default Content;
